// Edit metadata fields of an existing license:
//   - paymentStatus + paymentReference  (the common case: cập nhật khi khách thanh toán)
//   - plan, features, domain, maxInstances, notes
//
// expiresAt is NOT editable here — use Extend (preserves audit trail of renewals).
// key + secret are immutable post-issue (use regenerate-key if compromised).
//
// Implementation note: the form lives in a child component that is mounted
// ONLY when `license` is set. This way:
//   1. `initialValues` is applied on the very first render (no need for
//      useEffect + setFieldsValue, which fired before the Form instance was
//      attached when Modal had destroyOnClose).
//   2. Picking a different row remounts the inner form (key=license.id) so
//      stale field values from a previous row don't leak in.

import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
  message,
} from 'antd';
import type { License } from './licenses.api';
import { useUpdateLicense } from './useLicenses';

const { Text } = Typography;

interface Props {
  open: boolean;
  license: License | null;
  onClose: () => void;
}

interface FormValues {
  plan: 'basic' | 'pro' | 'enterprise';
  features: string[];
  domain: string | null;
  maxInstances: number;
  paymentStatus: 'unpaid' | 'paid' | 'refunded';
  paymentReference: string;
  notes: string;
}

const FEATURE_OPTIONS = [
  { value: 'whiteLabel', label: 'White-label' },
  { value: 'multiUser', label: 'Multi-user' },
  { value: 'sftp', label: 'SFTP storage' },
  { value: 's3', label: 'S3 storage' },
];

/** Inner form. Mounted only when `license` is non-null, so initialValues
 *  applies cleanly on mount. */
function EditLicenseForm({
  license,
  onSuccess,
  onCancel,
}: {
  license: License;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [form] = Form.useForm<FormValues>();
  const update = useUpdateLicense();

  const initialValues: FormValues = {
    plan: license.plan,
    features: license.features ?? [],
    domain: license.domain ?? '',
    maxInstances: license.maxInstances ?? 1,
    paymentStatus: license.paymentStatus,
    paymentReference: license.paymentReference ?? '',
    notes: license.notes ?? '',
  };

  async function handleOk() {
    try {
      const values = await form.validateFields();
      await update.mutateAsync({
        id: license.id,
        input: {
          ...values,
          domain: values.domain || null,
        },
      });
      message.success('Đã cập nhật');
      onSuccess();
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      if (apiMessage) message.error(apiMessage);
    }
  }

  return (
    <Modal
      open
      title={`Sửa license ${license.key}`}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      okText="Lưu"
      cancelText="Huỷ"
      confirmLoading={update.isPending}
      destroyOnClose
      width={560}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <>
            Để gia hạn dùng nút <Text strong>Extend</Text>, để thu hồi dùng nút{' '}
            <Text strong>Revoke</Text>. Form này chỉ sửa metadata.
          </>
        }
      />
      <Form<FormValues>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initialValues}
      >
        <Form.Item name="paymentStatus" label="Trạng thái thanh toán" rules={[{ required: true }]}>
          <Select
            getPopupContainer={(t) => t.parentElement ?? document.body}
            options={[
              { value: 'unpaid', label: 'Chưa thanh toán' },
              { value: 'paid', label: 'Đã thanh toán' },
              { value: 'refunded', label: 'Đã hoàn tiền' },
            ]}
          />
        </Form.Item>
        <Form.Item name="paymentReference" label="Mã hợp đồng / Mã giao dịch">
          <Input placeholder="HD-2026-001 / VCB-12345..." allowClear />
        </Form.Item>
        <Form.Item name="plan" label="Plan" rules={[{ required: true }]}>
          <Select
            getPopupContainer={(t) => t.parentElement ?? document.body}
            options={[
              { value: 'basic', label: 'Basic' },
              { value: 'pro', label: 'Pro' },
              { value: 'enterprise', label: 'Enterprise' },
            ]}
          />
        </Form.Item>
        <Form.Item name="features" label="Features">
          <Select
            mode="multiple"
            options={FEATURE_OPTIONS}
            getPopupContainer={(t) => t.parentElement ?? document.body}
            placeholder="Chọn features (optional)"
          />
        </Form.Item>
        <Form.Item name="domain" label="Domain lock" extra="Để trống nếu không khóa">
          <Input placeholder="tours.customer.com" allowClear />
        </Form.Item>
        <Form.Item name="maxInstances" label="Max instances">
          <InputNumber min={1} max={100} />
        </Form.Item>
        <Form.Item name="notes" label="Ghi chú">
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Outer wrapper — only renders the inner Modal+Form when license is set,
 *  so the form picks up initialValues correctly on mount. */
export function EditLicenseModal({ open, license, onClose }: Props) {
  if (!open || !license) return null;
  return (
    <EditLicenseForm
      key={license.id}
      license={license}
      onSuccess={onClose}
      onCancel={onClose}
    />
  );
}
